import { DragEndEvent } from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';
import { ReferenceBrandType } from '@1po/1po-bff-fe-spec/generated/catalog/references/common/model/ReferenceBrandType';
import {
  GET_MY_STORE_BUNDLES_RESPONSE,
  GET_MY_STORE_REFERENCES_RESPONSE,
  GET_MY_STORE_TIRES_RESPONSE,
} from '@1po/1po-bff-fe-spec/generated/common/ResponseType';
import {
  GetMyStoreBundlesResponse,
  MyStoreBundle,
  MyStoreSection,
} from '@1po/1po-bff-fe-spec/generated/my_store/bundles/response/GetMyStoreBundlesResponse';
import { CreateMyStoreReference } from '@1po/1po-bff-fe-spec/generated/my_store/references/request/CreateMyStoreReference';
import { UpdateMyStoreReference } from '@1po/1po-bff-fe-spec/generated/my_store/references/request/UpdateMyStoreReference';
import {
  GetMyStoreReferencesResponse,
  MyStoreReference,
  MyStoreReferencesSection,
} from '@1po/1po-bff-fe-spec/generated/my_store/references/response/GetMyStoreReferencesResponse';
import { MyStoreTireDiscount } from '@1po/1po-bff-fe-spec/generated/my_store/tires/model/MyStoreTireDiscount';
import { MyStoreTireRimSizeType } from '@1po/1po-bff-fe-spec/generated/my_store/tires/model/MyStoreTireRimSizeType';
import { MyStoreTireBrand } from '@1po/1po-bff-fe-spec/generated/my_store/tires/response/GetMyStoreTiresResponse';
import { createAction, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { createSelector } from 'reselect';
import { RootState } from 'app/AppStore';

import {
  ADD_BUNDLE_REQUEST,
  ADD_BUNDLES_SECTION_REQUEST,
  ADD_REFERENCE_REQUEST,
  ADD_REFERENCES_SECTION_REQUEST,
  BundleUpdateType,
  CREATE_OR_UPDATE_REFERENCE,
  CREATE_REFERENCE_REQUEST,
  GET_BUNDLES_REQUEST,
  GET_REFERENCES_REQUEST,
  GET_TIRES_REQUEST,
  INIT_MY_STORE,
  MOVE_BUNDLES_SECTION_REQUEST,
  MOVE_REFERENCES_SECTION_REQUEST,
  MY_STORE_NAMESPACE,
  MyStoreBundles,
  MyStoreReferences,
  MyStoreState,
  MyStoreTireBrandLocal,
  MyStoreTireRequestData,
  MyStoreTires,
  ReferenceUpdateType,
  REMOVE_BUNDLE_REQUEST,
  REMOVE_BUNDLE_SECTION_REQUEST,
  REMOVE_REFERENCE_REQUEST,
  REMOVE_REFERENCE_SECTION_REQUEST,
  RENAME_BUNDLES_SECTION_REQUEST,
  RENAME_REFERENCES_SECTION_REQUEST,
  RowUpdateType,
  UPDATE_BUNDLE_CODE_REQUEST,
  UPDATE_BUNDLE_DESIGNATION_REQUEST,
  UPDATE_BUNDLE_PRICE_REQUEST,
  UPDATE_REFERENCE_REQUEST,
  UPDATE_TIRE_DISCOUNT_LIST_REQUEST,
} from 'domains/myStore/MyStore.types';
import { SEARCH_STATUS } from 'utils';

const isIndexFound = (i: number) => i > -1;

// Init state
const initialState: MyStoreState = {
  bundles: {
    sections: {
      searchStatus: undefined,
    },
    selectedBundleIds: new Set<string>(),
  },
  references: {
    sections: {
      searchStatus: undefined,
    },
    selectedReferenceIds: new Set<string>(),
  },
  tires: {
    brands: {
      searchStatus: undefined,
    },
  },
  tiresDiscountChanges: [],
};

// Saga actions
export const initMyStoreRequest = createAction(INIT_MY_STORE);

// BUNDLES
export const getBundlesRequestSaga = createAction(GET_BUNDLES_REQUEST);
export const getBundlesResponseSaga = createAction(GET_MY_STORE_BUNDLES_RESPONSE);
export const addBundlesSectionRequestSaga = createAction(ADD_BUNDLES_SECTION_REQUEST);
export const moveBundlesSectionRequestSaga = createAction<DragEndEvent>(MOVE_BUNDLES_SECTION_REQUEST);
export const renameBundlesSectionRequestSaga = createAction<{ sectionId: string; title: string }>(
  RENAME_BUNDLES_SECTION_REQUEST,
);
export const addBundleRequestSaga = createAction<{ sectionId: string }>(ADD_BUNDLE_REQUEST);
export const removeBundleSectionReuestSaga = createAction<{ sectionId: string }>(REMOVE_BUNDLE_SECTION_REQUEST);
export const removeBundleRequestSaga = createAction<RowUpdateType>(REMOVE_BUNDLE_REQUEST);
export const updateBundleDesignationRequestSaga = createAction<BundleUpdateType & { bundleDesignation: string }>(
  UPDATE_BUNDLE_DESIGNATION_REQUEST,
);
export const updateBundleCodeRequestSaga = createAction<BundleUpdateType & { bundleCode: string }>(
  UPDATE_BUNDLE_CODE_REQUEST,
);
export const updateBundlePriceRequestSaga = createAction<BundleUpdateType & { bundlePrice: number }>(
  UPDATE_BUNDLE_PRICE_REQUEST,
);

// REFERENCES
export const getReferencesRequestSaga = createAction(GET_REFERENCES_REQUEST);
export const getReferencesResponseSaga = createAction(GET_MY_STORE_REFERENCES_RESPONSE);
export const addReferencesSectionRequestSaga = createAction(ADD_REFERENCES_SECTION_REQUEST);
export const moveReferencesSectionRequestSaga = createAction<DragEndEvent>(MOVE_REFERENCES_SECTION_REQUEST);
export const renameReferencesSectionRequestSaga = createAction<{ sectionId: string; title: string }>(
  RENAME_REFERENCES_SECTION_REQUEST,
);
export const addReferenceRequestSaga = createAction<{ sectionId: string }>(ADD_REFERENCE_REQUEST);
export const removeReferenceSectionRequestSaga = createAction<{ sectionId: string }>(REMOVE_REFERENCE_SECTION_REQUEST);
export const removeReferenceRequestSaga = createAction<RowUpdateType>(REMOVE_REFERENCE_REQUEST);
export const createOrUpdateReferenceSaga = createAction<ReferenceUpdateType>(CREATE_OR_UPDATE_REFERENCE);
export const createReferenceRequestSaga = createAction<CreateMyStoreReference>(CREATE_REFERENCE_REQUEST);
export const updateReferenceRequestSaga = createAction<UpdateMyStoreReference>(UPDATE_REFERENCE_REQUEST);

// TIRES
export const subscribeTiresRequestSaga = createAction(GET_TIRES_REQUEST);
export const getTiresResponseSaga = createAction(GET_MY_STORE_TIRES_RESPONSE);

export const updateMyStoreTireDiscountListRequest = createAction<MyStoreTireRequestData[]>(
  UPDATE_TIRE_DISCOUNT_LIST_REQUEST,
);

function getMyStoreTireBrandsMap(tires: MyStoreTires): Map<ReferenceBrandType, MyStoreTireBrandLocal> {
  return tires.brands.data ? tires.brands.data : new Map<ReferenceBrandType, MyStoreTireBrandLocal>();
}

function getMyStoreTireBrandDiscountsMap(
  brand?: MyStoreTireBrandLocal,
): Map<MyStoreTireRimSizeType, MyStoreTireDiscount> {
  return brand?.discounts ? brand.discounts : new Map<MyStoreTireRimSizeType, MyStoreTireDiscount>();
}

// Slice
const slice = createSlice({
  name: MY_STORE_NAMESPACE,
  initialState,
  reducers: {
    setInitialState: () => initialState,
    setBundlesStatus: (state, action: PayloadAction<SEARCH_STATUS>) => {
      state.bundles.sections.searchStatus = action.payload;
    },
    setBundlesSection: (state, action: PayloadAction<GetMyStoreBundlesResponse>) => {
      if (!state.bundles.myStoreBundlesId) {
        state.bundles.myStoreBundlesId = action.payload.myStoreBundlesId;
      }
      state.bundles.sections.data = [...action.payload.sections].sort((a, b) => a.index - b.index);
    },
    setBundleSelection: (
      state,
      { payload }: PayloadAction<{ sectionId: string; bundleId?: string; isSelected: boolean }>,
    ) => {
      const { sectionId, bundleId, isSelected } = payload;
      const section = state.bundles.sections.data?.find((s) => s.id === sectionId);
      const workingBundleIds = bundleId ? [bundleId] : section?.bundles?.map((s) => s.id) ?? [];
      const selectedIds = state.bundles.selectedBundleIds;
      workingBundleIds.forEach((bId) => {
        if (isSelected) {
          selectedIds.add(bId);
        } else {
          selectedIds.delete(bId);
        }
      });
    },
    reorderBundles: (state, { payload }: PayloadAction<DragEndEvent>) => {
      const { active, over } = payload;
      const sections = state.bundles.sections.data;
      if (active.id === over?.id) return;
      const originalPos = sections?.findIndex((s) => s.id === active.id) ?? -1;
      const newPos = sections?.findIndex((s) => s.id === over?.id) ?? -1;
      if (isIndexFound(originalPos) && isIndexFound(newPos) && sections) {
        const newArr = arrayMove(sections, originalPos, newPos);
        state.bundles.sections.data = newArr.map((section, index) => {
          return { ...section, index };
        });
      }
    },
    // MyStoreTires
    setMyStoreTiresId: (state, action: PayloadAction<{ myStoreTiresId: string }>) => {
      const { myStoreTiresId } = action.payload;
      state.tires.myStoreTiresId = myStoreTiresId;
    },
    setMyStoreTiresSearchStatus: (state, action: PayloadAction<SEARCH_STATUS>) => {
      state.tires.brands.searchStatus = action.payload;
    },
    setMyStoreTireBrands: (state, action: PayloadAction<{ brands: MyStoreTireBrand[] }>) => {
      const { brands } = action.payload;
      const brandsDataMap = getMyStoreTireBrandsMap(state.tires);

      brands.forEach((myStoreBrand) => {
        const brand = myStoreBrand.brand;
        const discountsMap = getMyStoreTireBrandDiscountsMap(brandsDataMap.get(brand));
        myStoreBrand.discounts.forEach((myStoreDiscount) => {
          const { rimSizeType, discount } = myStoreDiscount;
          if (rimSizeType) {
            discountsMap.set(rimSizeType, { rimSizeType, discount });
          }
        });
        brandsDataMap.set(brand, { brand, discounts: discountsMap });
      });
      state.tires.brands.data = brandsDataMap;
    },
    setMyStoreTiresChange: (state, action: PayloadAction<{ item: MyStoreTireRequestData }>) => {
      const { item } = action.payload;
      state.tiresDiscountChanges = [
        ...state.tiresDiscountChanges.filter((v) => {
          return !(v.tireRimSizeType === item.tireRimSizeType && v.brandId === item.brandId);
        }),
        item,
      ];
    },
    clearMyStoreTiresChanges: (state) => {
      state.tiresDiscountChanges = [];
    },
    // MyStoreReferences
    setReferencesStatus: (state, action: PayloadAction<SEARCH_STATUS>) => {
      state.references.sections.searchStatus = action.payload;
    },
    setReferencesSection: (state, action: PayloadAction<GetMyStoreReferencesResponse>) => {
      state.references.myStoreReferencesId ??= action.payload.myStoreReferencesId;
      state.references.sections.data = [...action.payload.sections].sort((a, b) => a.index - b.index);
    },
    addNewReference: (
      state,
      action: PayloadAction<{
        myStoreReferencesId: string;
        referenceId: string;
        sectionId: string;
      }>,
    ) => {
      const { sectionId, referenceId } = action.payload;
      const section = state.references.sections.data?.find((s) => s.sectionId === sectionId);
      if (!section) return;

      const newRef = {
        referenceId,
        referenceNumber: '',
        designation: '',
        clientPriceVatExcluded: '',
        garagePriceVatExcluded: '',
        isDraft: true,
      };

      if (section.references) {
        section.references.push(newRef);
      } else {
        section.references = [newRef];
      }
    },
    removeReference: (
      state,
      action: PayloadAction<{
        myStoreReferencesId: string;
        referenceId: string;
        sectionId: string;
      }>,
    ) => {
      const { referenceId, sectionId } = action.payload;
      const sections = state.references.sections.data;
      if (!sections) return;

      const section = sections.find((s) => s.sectionId === sectionId);
      if (!section?.references) return;

      section.references = section.references.filter((r) => r.referenceId !== referenceId);
    },
    updateReferenceInSection: (state, { payload }: PayloadAction<ReferenceUpdateType>) => {
      const sections = state.references.sections.data;
      if (!sections) return;

      const section = sections.find((s) => s.sectionId === payload.sectionId);
      if (!section?.references) return;

      const refIndex = section.references.findIndex((r) => r.referenceId === payload.referenceId);
      if (refIndex === -1) return;

      const existing = section.references[refIndex];
      section.references[refIndex] = { ...existing, ...payload.fieldUpdate };
    },
    setReferenceSelection: (
      state,
      { payload }: PayloadAction<{ sectionId: string; referenceId?: string; isSelected: boolean }>,
    ) => {
      const { sectionId, referenceId, isSelected } = payload;
      const section = state.references.sections.data?.find((s) => s.sectionId === sectionId);
      const workingReferenceIds = referenceId ? [referenceId] : section?.references?.map((s) => s.referenceId) ?? [];
      const selectedIds = state.references.selectedReferenceIds;
      workingReferenceIds.forEach((rId) => {
        if (isSelected) {
          selectedIds.add(rId);
        } else {
          selectedIds.delete(rId);
        }
      });
    },
    reorderReferences: (state, { payload }: PayloadAction<DragEndEvent>) => {
      const { active, over } = payload;
      const sections = state.references.sections.data;
      if (active.id === over?.id) return;
      const originalPos = sections?.findIndex((s) => s.sectionId === active.id) ?? -1;
      const newPos = sections?.findIndex((s) => s.sectionId === over?.id) ?? -1;
      if (isIndexFound(originalPos) && isIndexFound(newPos) && sections) {
        const newArr = arrayMove(sections, originalPos, newPos);
        state.references.sections.data = newArr.map((section, index) => {
          return { ...section, index };
        });
      }
    },
  },
});

// Actions
export const {
  setInitialState,
  setBundlesStatus,
  reorderBundles,
  setBundleSelection,
  setBundlesSection,
  setMyStoreTiresId,
  setMyStoreTiresSearchStatus,
  setMyStoreTireBrands,
  setMyStoreTiresChange,
  clearMyStoreTiresChanges,
  setReferencesStatus,
  setReferencesSection,
  reorderReferences,
  setReferenceSelection,
  addNewReference,
  updateReferenceInSection,
  removeReference,
} = slice.actions;

// Getters/Selectors
export const getBundlesSections = createSelector(
  (state: RootState) => state.myStore.bundles,
  (bundles: MyStoreBundles) => bundles,
);

export const getMyStoreBundlesId = createSelector(
  (state: RootState) => state.myStore.bundles.myStoreBundlesId,
  (myStoreBundlesId) => myStoreBundlesId,
);

export const getIsSectionSelected = createSelector(
  (state: RootState) => state.myStore.bundles.sections.data,
  (state: RootState) => state.myStore.bundles.selectedBundleIds,
  (_state: RootState, sectionId: string) => sectionId,
  (sections, selectedBundleIds, sectionId) => {
    const section = sections?.find((s) => s.id === sectionId);
    return (
      (section?.bundles &&
        section?.bundles.length > 0 &&
        section?.bundles?.every((bundle) => selectedBundleIds.has(bundle.id))) ??
      false
    );
  },
);

export const getIsBundleSelected = createSelector(
  (state: RootState) => state.myStore.bundles.selectedBundleIds,
  (_state: RootState, bundleId?: string) => bundleId,
  (selectedBundleIds, bundleId) => {
    if (!bundleId) return false;
    return selectedBundleIds.has(bundleId);
  },
);

export const getAllSelectedBundles = createSelector(
  (state: RootState) => state.myStore.bundles.sections.data,
  (state: RootState) => state.myStore.bundles.selectedBundleIds,
  (sections, selectedBundleIds) => {
    const all =
      sections?.reduce((result: MyStoreBundle[], section: MyStoreSection) => {
        const allSectionBundles = section.bundles?.map((b) => b ?? []).flat() ?? [];
        return [...result, ...allSectionBundles];
      }, []) ?? [];

    return all.filter((i) => !!i.code && !!i.designation && !!i.price).filter((i) => selectedBundleIds.has(i.id));
  },
);

export const getAllBundleCodes = createSelector(
  (state: RootState) => state.myStore.bundles.sections.data,
  (sections) => {
    return (
      sections?.reduce((result: string[], section: MyStoreSection) => {
        const allSectionCodes = section.bundles?.map((b) => b.code ?? []).flat() ?? [];
        return [...result, ...allSectionCodes];
      }, []) ?? []
    );
  },
);

// My Store References
export const getReferencesSections = createSelector(
  (state: RootState) => state.myStore.references,
  (references: MyStoreReferences) => references,
);

export const getMyStoreReferencesId = createSelector(
  (state: RootState) => state.myStore.references.myStoreReferencesId,
  (myStoreReferencesId) => myStoreReferencesId,
);

export const getIsReferencesSectionSelected = createSelector(
  (state: RootState) => state.myStore.references.sections.data,
  (state: RootState) => state.myStore.references.selectedReferenceIds,
  (_state: RootState, sectionId: string) => sectionId,
  (sections, selectedReferenceIds, sectionId) => {
    const section = sections?.find((s) => s.sectionId === sectionId);
    return (
      (section?.references &&
        section?.references.length > 0 &&
        section?.references?.every((referenceSection) => selectedReferenceIds.has(referenceSection.referenceId))) ??
      false
    );
  },
);

export const getSectionReference = createSelector(
  (state: RootState) => state.myStore.references.sections.data,
  (
    _state: RootState,
    params: {
      sectionId: string;
      referenceId: string;
    },
  ) => params,
  (sections, params) => {
    const { sectionId, referenceId } = params;
    const section = sections?.find((s) => s.sectionId === sectionId);
    if (section?.references && section?.references.length > 0) {
      return section?.references?.find((referenceSection) => referenceSection.referenceId === referenceId);
    }
    return undefined;
  },
);

export const getIsReferenceSelected = createSelector(
  (state: RootState) => state.myStore.references.selectedReferenceIds,
  (_state: RootState, referenceId?: string) => referenceId,
  (selectedReferenceIds, referenceId) => {
    if (!referenceId) return false;
    return selectedReferenceIds.has(referenceId);
  },
);

export const getAllSelectedReferences = createSelector(
  (state: RootState) => state.myStore.references.sections.data,
  (state: RootState) => state.myStore.references.selectedReferenceIds,
  (sections, selectedReferenceIds) => {
    const all =
      sections?.reduce((result: MyStoreReference[], section: MyStoreReferencesSection) => {
        const allSectionReferences = section.references?.map((b) => b ?? []).flat() ?? [];
        return [...result, ...allSectionReferences];
      }, []) ?? [];

    return all
      .filter((i) => !!i.referenceNumber && !!i.designation && !!i.clientPriceVatExcluded && !!i.garagePriceVatExcluded)
      .filter((i) => selectedReferenceIds.has(i.referenceId));
  },
);

export const getAllReferenceNumbers = createSelector(
  (state: RootState) => state.myStore.references.sections.data,
  (sections) => {
    return (
      sections?.reduce((result: string[], section: MyStoreReferencesSection) => {
        const allSectionReferenceNumbers = section.references?.map((b) => b.referenceNumber ?? []).flat() ?? [];
        return [...result, ...allSectionReferenceNumbers];
      }, []) ?? []
    );
  },
);

// My Store Tires
export const getMyStoreTires = createSelector(
  (state: RootState) => state.myStore.tires,
  (tires) => tires,
);

export const getMyStoreTiresId = createSelector(
  (state: RootState) => state.myStore.tires.myStoreTiresId,
  (myStoreTiresId) => myStoreTiresId,
);

export const getMyStoreTiresDiscount = createSelector(
  (state: RootState) => state.myStore.tires,
  (_state: RootState, params: { brand: ReferenceBrandType; tireRimSizeType: MyStoreTireRimSizeType }) => params,
  (tires, params) => {
    const { brand, tireRimSizeType } = params;
    const myStoreBrand = tires.brands?.data?.get(brand);
    if (myStoreBrand) {
      return myStoreBrand.discounts.get(tireRimSizeType);
    }
    return undefined;
  },
);

export const getMyStoreTiresDiscountChange = createSelector(
  (state: RootState) => state.myStore.tiresDiscountChanges,
  (_state: RootState, params: { brand: ReferenceBrandType; tireRimSizeType: MyStoreTireRimSizeType }) => params,
  (discounts: MyStoreTireRequestData[], params) => {
    const { brand, tireRimSizeType } = params;
    return discounts.find((i) => i.tireRimSizeType === tireRimSizeType && i.brandId === brand)?.discount;
  },
);

export const getMyStoreTiresDiscountChanges = createSelector(
  (state: RootState) => state.myStore.tiresDiscountChanges,
  (changes) => changes,
);

export const getMyStoreTiresSearchStatus = createSelector(
  (state: RootState) => state.myStore.tires.brands.searchStatus,
  (searchStatus) => searchStatus,
);

export default slice.reducer;
